import { DateTime, Settings } from "luxon";
Settings.defaultLocale = "en";

window.customDatePicker = function (initialIsoDate) {
  return {
    isOpen: false,
    selectedDateIso: initialIsoDate, // Holds the YYYY-MM-DD string
    viewDate: DateTime.fromISO(initialIsoDate), // The month currently displayed in the UI
    daysOfWeek: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],

    init() {
      // Watch global store changes made by the 'next'/'prev' buttons
      this.$watch("$store.date.date", (newIsoDate) => {
        this.selectedDateIso = newIsoDate;
        this.viewDate = DateTime.fromISO(newIsoDate);
      });
    },

    get currentMonthYear() {
      return this.viewDate.toLocaleString({ month: "long", year: "numeric" });
    },

    get formattedDate() {
      return DateTime.fromISO(this.selectedDateIso).toLocaleString(
        DateTime.DATE_FULL,
      );
    },

    get calendarDays() {
      const startOfMonth = this.viewDate.startOf("month");
      const endOfMonth = this.viewDate.endOf("month");
      const days = [];

      // Calculate start of first week (Sunday = 0)
      let dayCursor = startOfMonth.startOf("week");

      while (dayCursor <= endOfMonth.endOf("week")) {
        days.push({
          dateObj: dayCursor,
          isOutsideMonth: dayCursor.month !== this.viewDate.month,
          isSelected: dayCursor.toISODate() === this.selectedDateIso,
        });
        dayCursor = dayCursor.plus({ days: 1 });
      }
      return days;
    },

    selectDate(day) {
      this.selectedDateIso = day.dateObj.toISODate();
      this.viewDate = day.dateObj; // Update viewDate to stay on selected month
      this.isOpen = false;

      // Sync this local change back to the global store
      this.$store.date.date = this.selectedDateIso;
    },

    nextMonth() {
      this.viewDate = this.viewDate.plus({ months: 1 });
    },

    previousMonth() {
      this.viewDate = this.viewDate.minus({ months: 1 });
    },

    goToToday() {
      const todayIso = DateTime.now().toISODate();

      // Re-use selectDate logic to update everything correctly
      this.selectDate({
        dateObj: DateTime.fromISO(todayIso),
        // No need for isOutsideMonth/isSelected here, selectDate handles that
      });
      this.isOpen = true;
    },

    get dateJSObject() {
      // Step 1: ISO string → Luxon DateTime in your timezone
      const dt = DateTime.fromISO(this.date, {
        zone: Alpine.store("location").timezone,
      });

      // Step 2: Luxon → JS Date
      return dt.toJSDate();
    },
  };
};

export function Date() {
  return {
    date: null,
    isOpen: false,
    _isInitialLoad: true, // Flag to prevent immediate scroll
    init() {
      this.date = this.today();
      Alpine.effect(() => {
        const current = this.date;
        // Fire the global signal
        // Only dispatch if this isn't the first time the app is setting the date
        if (!this._isInitialLoad) {
          window.dispatchEvent(new CustomEvent("date-changed"));
        }

        this._isInitialLoad = false;
      });
    },

    today() {
      // Returns the ISO string format needed for HTML date input
      return DateTime.now()
        .setZone(Alpine.store("location").timezone)
        .toISODate();
    },

    nextDate() {
      const currentLuxonDate = DateTime.fromISO(this.date, {
        zone: Alpine.store("location").timezone,
      });
      this.date = currentLuxonDate.plus({ days: 1 }).toISODate();
    },
    previousDate() {
      const currentLuxonDate = DateTime.fromISO(this.date, {
        zone: Alpine.store("location").timezone,
      });
      this.date = currentLuxonDate.minus({ days: 1 }).toISODate();
    },

    recomputeDate() {
      // Return the new ISO date string, the watcher handles the assignment to this.date
      const currentLuxonDate = DateTime.fromISO(this.date, {
        zone: Alpine.store("location").timezone,
      }).setZone(Alpine.store("location").timezone);
      return currentLuxonDate.toISODate();
    },
  };
}
